/**
 * Values fixed by the spec. Anything here is a decision, not a tuning knob —
 * see docs/spec-v3.md and docs/decision-log.md before changing one.
 */

/** Per-bot player numbering starts here (spec §18.2, decision 37). */
export const FIRST_PLAYER_NO = 1;

/**
 * How a player number reads everywhere — Topic names and the Admin Panel:
 * at least two digits, so #01…#99, then #100 and on (decision 37).
 */
export function formatPlayerNo(playerNo: number): string {
  return `#${String(playerNo).padStart(2, "0")}`;
}

/** Telegram's hard limit on a forum topic name (spec §5.3). */
export const TOPIC_NAME_MAX_LENGTH = 128;

/** Shown instead of an empty Telegram display name (spec §5.3). */
export const FALLBACK_DISPLAY_NAME = "Player";

/** Prefix on a banned player's topic name (spec §6.2). */
export const BAN_TOPIC_PREFIX = "[BAN]";

/** Album parts are buffered this long after the last part arrives (spec §5.7). */
export const ALBUM_BUFFER_MS = 1_500;

/** CS → player: give up after this and ask the agent to resend (spec §5.6). */
export const CS_TO_PLAYER_RETRY_WINDOW_MS = 60_000;

/**
 * Player → Topic: how many times one message is attempted before we give up
 * on it and let the queue move on (decision 49).
 *
 * Counted in **attempts on that message**, never in wall-clock since it
 * arrived. The Durable Object runs one item at a time in order, so a message
 * can sit behind another for an hour without ever being tried — and a backlog
 * released after a Telegram outage is entirely "old" while being entirely
 * untried. Judging it by age would discard the whole backlog on first contact,
 * which is a worse failure than the jam this replaced.
 *
 * With the backoff in do/outcome.ts (500ms doubling to a 30s ceiling), eight
 * attempts is roughly two minutes of genuinely trying.
 *
 * Giving up does not lose the message: the text is still recorded and the
 * Topic is told what happened.
 */
export const PLAYER_TO_TOPIC_MAX_ATTEMPTS = 8;

/** Telegram refuses to delete a message older than this (spec §6.1.3). */
export const REVOKE_MAX_AGE_MS = 48 * 60 * 60 * 1000;

/** Broadcast fan-out, kept under Telegram's ~30/s per bot (spec §11). */
export const BROADCAST_MESSAGES_PER_SECOND = 25;

/** Deep-link payload charset Telegram accepts (spec §8, ticket 17). */
export const START_PAYLOAD_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/** Auto-created per bot; a player with no attribution lands here (spec §8). */
export const OFFICIAL_SOURCE_CODE = "official";

/**
 * What a Topic name says in place of a source for an official or unattributed
 * player, so every Topic name has the same shape (decision 53).
 */
export const DIRECT_SOURCE_LABEL = "Direct";
